import React, { useState, useContext, useEffect } from 'react';
import VerseText from './VerseText';
import AudioPlayer from './AudioPlayer';
import NavigationPopover from './NavigationPopover';
import { AudioContext } from '../contexts/AudioContext';
import { getRecentListeningRanges } from '../db';
import { parseChapterHash } from '../utils/chapterHash';
import './MainScreen.css';

function MainScreen({ onLogout, isOnline }) {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [navDefaultTab, setNavDefaultTab] = useState('books');
  const { currentChapter } = useContext(AudioContext);

  // Initial startup logic
  useEffect(() => {
    const initNavigation = async () => {
      const hashChapter = parseChapterHash(window.location.hash);
      try {
        const ranges = await getRecentListeningRanges();
        if (ranges && ranges.length > 0) {
          setNavDefaultTab('recent');
        } else {
          setNavDefaultTab('books');
        }
        // Hash restore is already starting a chapter — don't cover it with recents
        if (!hashChapter) {
          setIsNavOpen(true);
        }
      } catch (error) {
        console.error("Failed to load recent listens for startup:", error);
        setNavDefaultTab('books');
        if (!hashChapter) {
          setIsNavOpen(true);
        }
      }
    };
    
    // Only run this once on mount
    initNavigation();
  }, []);

  // Helper function to check if navigation state exists in history
  const hasNavigationState = () => {
    return window.history.state?.navigationPopover !== undefined;
  };

  const handleCloseNav = (shouldForce) => {
    // If navigation is open and we have history entries from navigation, go back
    // Unless forced to close (e.g. searching/selecting an item)
    if (shouldForce !== true && isNavOpen && hasNavigationState()) {
      window.history.back();
    } else {
      setIsNavOpen(false);
    }
  };

  const toggleNav = () => {
    if (isNavOpen) {
      handleCloseNav();
    } else {
      setIsNavOpen(true);
    }
  };

  // Listen for when navigation should actually close (via popstate or direct close)
  useEffect(() => {
    const handlePopState = () => {
      // If nav is open and there's no navigation state in history, close it
      if (isNavOpen && !hasNavigationState()) {
        setIsNavOpen(false);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isNavOpen]);

  return (
    <div className="main-screen">
      <button className="burger-menu" onClick={toggleNav}>
        ☰
      </button>
      <NavigationPopover 
        isOpen={isNavOpen} 
        onClose={handleCloseNav} 
        onLogout={onLogout}
        isOnline={isOnline}
        defaultTab={navDefaultTab}
      />
      <div className="content">
        {currentChapter && (
          <div className="chapter-title">
            {currentChapter.book} {currentChapter.chapter}
          </div>
        )}
        <VerseText onUnauthorized={onLogout} />
        <AudioPlayer />
      </div>
    </div>
  );
}

export default MainScreen;
